/**
 * Mandatory MFA enrolment (M02-04): scan the QR code, confirm with a first
 * code, then save the recovery codes — they are shown exactly once.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, type SessionUser } from '../lib/auth';
import { Alert, Button, Card, Field, Skeleton, inputClass } from '../components/ui';
import { AuthLayout } from './AuthLayout';

interface Setup {
  secret: string;
  otpauthUri: string;
  qrDataUrl: string;
}

export function MfaSetupPage(): JSX.Element {
  const { user, adoptSession, signOut } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.mfaEnabled && !recovery) navigate('/', { replace: true });
  }, [user, recovery, navigate]);

  useEffect(() => {
    if (user && !user.mfaEnabled) {
      api<Setup>('/auth/mfa/setup', { method: 'POST' })
        .then(setSetup)
        .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not start the set-up.'));
    }
    // Only once per visit: a new secret each time would break a half-done scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirm = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = await api<{ accessToken: string; user: SessionUser; recoveryCodes: string[] }>('/auth/mfa/enable', {
        method: 'POST',
        body: { code },
      });
      setRecovery(body.recoveryCodes);
      adoptSession(body.accessToken, body.user);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work. Try the next code.');
    } finally {
      setBusy(false);
    }
  };

  if (recovery) {
    return (
      <AuthLayout>
        <Card title="Save your recovery codes" description="If you lose your phone, each code signs you in once. They will not be shown again.">
          <ol className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-4 font-mono text-sm dark:bg-slate-800">
            {recovery.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => void navigator.clipboard?.writeText(recovery.join('\n'))}>
              <Copy aria-hidden="true" className="h-4 w-4" /> Copy
            </Button>
          </div>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            I have stored these codes somewhere safe.
          </label>
          <Button className="mt-4 w-full min-h-11" disabled={!saved} onClick={() => navigate('/', { replace: true })}>
            Continue to CoreX-lite
          </Button>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card title="Turn on two-step sign-in" description="CoreX-lite holds your books and tax data, so every account uses an authenticator app.">
        {error && (
          <div className="mb-4">
            <Alert tone="critical">{error}</Alert>
          </div>
        )}
        <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
          <li>Install Google Authenticator, Microsoft Authenticator or Authy on your phone.</li>
          <li>Scan this code with the app.</li>
          <li>Type the 6-digit code it shows.</li>
        </ol>
        <div className="flex justify-center">
          {setup ? (
            <img src={setup.qrDataUrl} alt="QR code to add CoreX-lite to your authenticator app" className="h-52 w-52 rounded-xl bg-white p-2" />
          ) : (
            <Skeleton className="h-52 w-52" />
          )}
        </div>
        {setup && (
          <p className="mt-3 break-all text-center text-xs text-slate-500">
            Can't scan? Enter this key: <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{setup.secret}</span>
          </p>
        )}
        <form className="mt-5 space-y-4" onSubmit={(e) => void onConfirm(e)} noValidate>
          <Field label="Code from the app" required>
            <input
              className={`${inputClass} text-center text-lg tracking-[0.3em]`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Button type="submit" busy={busy} disabled={code.length !== 6} className="w-full min-h-11">
            <ShieldCheck aria-hidden="true" className="h-4 w-4" /> Turn on
          </Button>
          <button type="button" className="w-full text-sm text-slate-500 hover:underline" onClick={() => void signOut().then(() => navigate('/login'))}>
            Sign out
          </button>
        </form>
      </Card>
    </AuthLayout>
  );
}
